import { useEffect, useState } from 'react';
import { FaArrowDown, FaArrowUp, FaPencilAlt, FaRedo } from 'react-icons/fa';
import { format } from 'date-fns';
import { Button, Col, Input, Modal, ModalBody, ModalHeader, Row } from 'reactstrap';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  ColumnDef,
  SortingState,
} from '@tanstack/react-table';

import { UpdateTransactionDto } from '@/services/transactions/transactions-types';
import { updateTableTransactionRest } from '@/services/table/table-service';
import { retryTransactionRest, getTransactionRetryStatus } from '@/services/transactions/transactions-service';
import { showToast } from '@/common/showToast';
import SkeletonRow from '@/components/data-table/SkeletonRow';
import { Transaction, TransactionDto, TransactionStatus, TransactionType } from '@repo/common-types';

import Paginator from './paginator';
import SortableTableHeader from '@/components/data-table/SortableTableHeader';

// Transaction types that support retry
const RETRYABLE_TRANSACTION_TYPES = [
  'LEAVE_TABLE',
  'RECOVER_FUNDS',
  'TABLE_REFUND',
  'TOPUP_REFUND',
  'REBUY_REFUND',
  'COLLECT_RAKE',
  'FUND_TABLE',
  'DEPOSIT_BONUS',
] as const;

/**
 * Retry button component with loading state and 30-second UI cooldown
 */
const RetryButton = ({ transactionId, onRetrySuccess }: { transactionId: string; onRetrySuccess?: () => void }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [cooldownTime, setCooldownTime] = useState<number>(0);
  const [tooltipMessage, setTooltipMessage] = useState<string>('Retry transaction');

  // Check retry status on mount and periodically
  useEffect(() => {
    const checkRetryStatus = async () => {
      try {
        const status = await getTransactionRetryStatus(transactionId);
        if (!status.canRetry && status.remainingCooldownSeconds) {
          setIsDisabled(true);
          setCooldownTime(Math.min(status.remainingCooldownSeconds, 600)); // Cap at 10 minutes for display
          setTooltipMessage(status.message || 'Please wait before retrying');
        }
      } catch (error) {
        // Silent fail - don't block UI if status check fails
      }
    };

    checkRetryStatus();
    // Check again every 30 seconds if disabled
    const interval = isDisabled ? setInterval(checkRetryStatus, 30000) : null;

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [transactionId, isDisabled]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (cooldownTime > 0) {
      interval = setInterval(() => {
        setCooldownTime((prev) => {
          if (prev <= 1) {
            setIsDisabled(false);
            setTooltipMessage('Retry transaction');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [cooldownTime]);

  const handleRetry = async () => {
    setIsLoading(true);
    try {
      await retryTransactionRest(transactionId);
      showToast('Transaction retry initiated successfully', 'success');

      // Start 30-second UI cooldown
      setIsDisabled(true);
      setCooldownTime(30);
      setTooltipMessage('Please wait before retrying again');

      if (onRetrySuccess) {
        onRetrySuccess();
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Failed to retry transaction';
      showToast(errorMessage, 'warning');

      // If it's a rate limit error, extract the cooldown time
      if (errorMessage.includes('rate limited')) {
        const match = errorMessage.match(/wait (\d+) minutes/);
        if (match) {
          const minutes = parseInt(match[1]);
          setCooldownTime(minutes * 60);
          setIsDisabled(true);
          setTooltipMessage(errorMessage);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formatCooldownDisplay = () => {
    if (cooldownTime >= 60) {
      const minutes = Math.floor(cooldownTime / 60);
      const seconds = cooldownTime % 60;
      return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
    }
    return `${cooldownTime}s`;
  };

  return (
    <Button
      color="danger"
      size="sm"
      onClick={handleRetry}
      disabled={isLoading || isDisabled}
      className="d-flex align-items-center justify-content-center"
      style={{ minWidth: cooldownTime > 0 ? '80px' : '40px', height: '32px' }}
      title={tooltipMessage}
    >
      {isLoading ? (
        <div className="spinner-border spinner-border-sm" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      ) : cooldownTime > 0 ? (
        <span style={{ fontSize: '12px' }}>{formatCooldownDisplay()}</span>
      ) : (
        <FaRedo />
      )}
    </Button>
  );
};

const EditTransactionModal = ({
  refreshTable,
  selectedTx,
  closeModal,
}: {
  refreshTable?: () => void;
  selectedTx: Transaction;
  closeModal: () => void;
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<UpdateTransactionDto>({
    defaultValues: {
      status: selectedTx.status,
      txId: selectedTx.txId ?? undefined,
      message: selectedTx.message ?? undefined,
      type: (selectedTx.type as any) ?? undefined,
    },
  });

  // Keep amount as a string to allow typing decimals gracefully (e.g., "12." or ".5")
  const [amountInput, setAmountInput] = useState<string>(selectedTx.amount != null ? String(selectedTx.amount) : '');

  const [backendError, setBackendError] = useState<string | null>(null);
  const [showError, setShowError] = useState<boolean>(true);

  const setError = (error: { response: { data: { message: string } } } | null) => {
    if (error?.response?.data?.message) {
      setBackendError(error.response.data.message);
    } else {
      setBackendError('An unexpected error occurred.');
    }
    setShowError(true);
  };

  const onSubmit = async (data: UpdateTransactionDto) => {
    try {
      const updates: UpdateTransactionDto = {};

      if (data.status && data.status !== selectedTx.status) updates.status = data.status;
      if ((data.txId ?? '') !== (selectedTx.txId ?? '')) updates.txId = data.txId;
      if ((data.message ?? '') !== (selectedTx.message ?? '')) updates.message = data.message;
      // Parse amountInput only on submit; allow dot decimals while typing
      const trimmedAmount = amountInput.trim();
      if (trimmedAmount !== '') {
        const normalizedAmount = Number(trimmedAmount);
        if (Number.isNaN(normalizedAmount)) {
          showToast('Invalid amount format', 'warning');
          return;
        }
        if (normalizedAmount !== selectedTx.amount) updates.amount = normalizedAmount;
      }
      if (data.type && data.type !== (selectedTx.type as any)) updates.type = data.type as any;

      if (Object.keys(updates).length === 0) {
        showToast('No changes to update', 'warning');
        return;
      }

      await updateTableTransactionRest(selectedTx.id, updates);
      closeModal();
      showToast('Transaction updated successfully', 'success');
      if (refreshTable) {
        refreshTable();
      }
    } catch (error: any) {
      setShowError(false);
      setTimeout(() => {
        setError(error);
      }, 100);
    }
  };

  return (
    <Modal isOpen backdrop="static" id="editTx" centered>
      <ModalHeader className="bg-dark p-3" toggle={closeModal}>
        Edit Transaction
      </ModalHeader>
      <ModalBody>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Col>
            <Col className="mb-4">
              <Row>
                <div className="form-field">
                  <label htmlFor="status">Status</label>
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <select {...field} className="form-control">
                        <option value=""></option>
                        {Object.keys(TransactionStatus).map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                </div>
              </Row>
            </Col>
            <Col className="mb-4">
              <Row>
                <div className="form-field">
                  <label htmlFor="txId">Transaction Id</label>
                  <Controller
                    name="txId"
                    control={control}
                    render={({ field }) => <Input {...field} className="form-control" placeholder="0x..." />}
                  />
                </div>
              </Row>
            </Col>
            <Col>
              <Row>
                <div className="form-field">
                  <label htmlFor="message">Message</label>
                  <Controller
                    name="message"
                    control={control}
                    render={({ field }) => <textarea {...field} className="form-control" />}
                  />
                </div>
              </Row>
            </Col>
            <Col className="mb-4">
              <Row>
                <div className="form-field">
                  <label htmlFor="amount">Amount</label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    placeholder="e.g. 123.45"
                    className="form-control"
                    value={amountInput}
                    onChange={(e) => {
                      let v = e.target.value.replace(',', '.');
                      v = v.replace(/[^0-9.]/g, '');
                      const firstDot = v.indexOf('.');
                      if (firstDot !== -1) {
                        v = v.substring(0, firstDot + 1) + v.substring(firstDot + 1).replace(/\./g, '');
                      }
                      setAmountInput(v);
                    }}
                  />
                </div>
              </Row>
            </Col>
            <Col className="mb-2">
              <Row>
                <div className="form-field">
                  <label htmlFor="type">Type</label>
                  <Controller
                    name="type"
                    control={control}
                    render={({ field }) => (
                      <select {...field} className="form-control">
                        <option value=""></option>
                        {Object.values(TransactionType).map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                </div>
              </Row>
            </Col>
          </Col>
          {backendError && showError && <div className="text-danger my-2">{backendError}</div>}
          <Col className="my-2 mt-4">
            <Button color="grey" onClick={closeModal}>
              Cancel
            </Button>
            <Button color="primary" type="submit">
              Submit
            </Button>
          </Col>
        </form>
      </ModalBody>
    </Modal>
  );
};

const TransactionsTable = ({
  refreshTable,
  transactions,
  totalCount,
  onPageChange,
  onSortingChange,
  isLoadingData,
  columns,
  take = 10,
  skip = 0,
  sorting,
}: {
  refreshTable?: () => void;
  transactions: TransactionDto[];
  totalCount: number;
  onPageChange: (skip: number, take: number) => void;
  onSortingChange: (updater: SortingState | ((old: SortingState) => SortingState)) => void;
  take?: number;
  skip?: number;
  isLoadingData?: boolean;
  columns: ColumnDef<TransactionDto>[];
  sorting?: SortingState;
}) => {
  const [selectedTx, setSelectedTx] = useState<Transaction | null>(null);

  const openEditModal = (tx: Transaction) => {
    setSelectedTx(tx);
  };

  const columnsWithActions = [
    ...columns,
    {
      header: 'Actions',
      cell: (info) => (
        <div className="d-flex justify-content-center gap-2">
          <Button color="primary" size="sm" onClick={() => openEditModal(info.row.original)}>
            <FaPencilAlt />
          </Button>
          {info.row.original.status === 'FAILED' &&
            RETRYABLE_TRANSACTION_TYPES.includes(info.row.original.type as any) &&
            (info.row.original.retries || 0) < 3 && (
              <RetryButton transactionId={info.row.original.id} onRetrySuccess={refreshTable} />
            )}
        </div>
      ),
      size: 150,
      meta: {
        className: 'justify-content-center',
      },
    },
  ];

  const table = useReactTable({
    data: transactions,
    columns: columnsWithActions,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange,
    manualPagination: true,
    manualSorting: true,
    rowCount: transactions.length,
    state: {
      sorting,
    },
  });

  return (
    <div>
      {selectedTx && (
        <EditTransactionModal
          selectedTx={selectedTx}
          closeModal={() => setSelectedTx(null)}
          refreshTable={refreshTable}
        />
      )}
      <div className="overflow-auto py-2">
        <table
          className="table-hover table-centered table-nowrap table align-middle"
          style={{ borderTop: '2px solid #000' }}
        >
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup?.headers.map((header) => (
                  <SortableTableHeader key={header.id} header={header} />
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {isLoadingData
              ? Array.from({ length: take }).map((_, index) => (
                  <SkeletonRow key={`skeleton-${index}`} columns={columnsWithActions} trStyle={{ height: 62 }} />
                ))
              : table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                    ))}
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
      <Paginator
        take={take}
        skip={skip}
        setTake={(_take) => onPageChange(skip, _take)}
        setSkip={(_skip) => onPageChange(_skip, take)}
        totalCount={totalCount}
        showing={transactions.length}
      />
    </div>
  );
};

export default TransactionsTable;
